import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

interface BlockProps {
	heading: string;
	// Rendered before the heading, so the wording can point at the very icon the
	// reader sees in the column rather than describing it in words.
	headingIcon?: ReactNode;
	intro: string;
	body: ReactNode;
	note: string;
}

/** One of the two explanations: what the date is, and when it is flagged. */
function InfoBlock({ heading, headingIcon, intro, body, note }: BlockProps) {
	return (
		<Box sx={{ padding: '12px 16px' }}>
			<Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
				{headingIcon}
				<Typography
					variant='body1'
					sx={{ fontWeight: 'bold', color: 'common.neutral20' }}
				>
					{heading}
				</Typography>
			</Box>
			<Typography
				variant='body2'
				sx={{ color: 'common.neutral50', marginTop: '2px' }}
			>
				{intro}
			</Typography>
			{body}
			<Typography
				variant='body2'
				sx={{ color: 'common.neutral50', marginTop: '8px', lineHeight: 1.55 }}
			>
				{note}
			</Typography>
		</Box>
	);
}

/**
 * The "i" beside the Last meeting intervention date column header. Which
 * interventions count towards the date, and what the alert icon next to it
 * means, are both rules staff cannot infer from the column itself.
 *
 * Both explanations live here rather than one of them on the alert icon: the
 * icon is only present on flagged rows, so anyone wondering why a row has no
 * icon - or no date - would have nothing to hover. The icon carries the reason
 * for that one row instead.
 */
export default function LastMeetingInfoHint() {
	const { t } = useTranslation();

	const content = (
		<Box sx={{ padding: '16px 8px 12px' }}>
			<Typography
				variant='subtitle1'
				sx={{
					fontWeight: 'bold',
					color: 'common.neutral20',
					padding: '0 16px 12px',
					borderBottom: '1px solid',
					borderColor: 'common.neutral90',
				}}
			>
				{t('member.lastMeetingInterventionDate')}
			</Typography>

			<Box sx={{ marginTop: '4px' }}>
				<InfoBlock
					heading={t('case.lastMeetingHintDateHeading')}
					intro={t('case.lastMeetingHintDateIntro')}
					body={
						<Typography
							variant='body2'
							sx={{
								color: 'common.neutral30',
								marginTop: '6px',
								lineHeight: 1.55,
							}}
						>
							{t('case.lastMeetingHintDateNatures')}
						</Typography>
					}
					note={t('case.lastMeetingHintDateNote')}
				/>

				<InfoBlock
					heading={t('case.lastMeetingHintAlertHeading')}
					headingIcon={
						<ErrorOutlineIcon
							sx={{ fontSize: '20px', color: 'common.neutral20' }}
						/>
					}
					intro={t('case.lastMeetingHintAlertIntro')}
					body={
						<Box
							component='ul'
							sx={{
								margin: '8px 0 0',
								paddingLeft: '20px',
								color: 'common.neutral30',
								'& li': { marginTop: '6px' },
								'& li::marker': { color: 'common.neutral70' },
							}}
						>
							{[
								t('case.lastMeetingHintAlertHigh'),
								t('case.lastMeetingHintAlertMedium'),
								t('case.lastMeetingHintAlertLow'),
							].map((point) => (
								<Typography
									key={point}
									component='li'
									variant='body2'
									sx={{ lineHeight: 1.55 }}
								>
									{point}
								</Typography>
							))}
						</Box>
					}
					note={t('case.lastMeetingHintAlertNote')}
				/>
			</Box>
		</Box>
	);

	return (
		<Tooltip
			title={content}
			arrow
			placement='bottom-start'
			enterDelay={200}
			leaveDelay={100}
			enterTouchDelay={0}
			leaveTouchDelay={15000}
			slotProps={{
				// Repaint MUI's dark hint bubble as a card in the grid's own
				// language: white surface, 16px radius, hairline border, soft shadow.
				tooltip: {
					sx: {
						width: '440px',
						maxWidth: 'calc(100vw - 48px)',
						padding: 0,
						backgroundColor: 'common.white',
						color: 'common.neutral30',
						borderRadius: '16px',
						border: '1px solid',
						borderColor: 'common.neutral90',
						boxShadow: '0px 8px 24px rgba(33, 37, 41, 0.12)',
					},
				},
				arrow: {
					sx: {
						color: 'common.white',
						'&::before': {
							border: '1px solid',
							borderColor: 'common.neutral90',
						},
					},
				},
			}}
		>
			<IconButton
				size='small'
				disableRipple
				aria-label={t('member.lastMeetingInterventionDate')}
				sx={{
					marginLeft: '4px',
					padding: '2px',
					// The header renders it inline after the title, so it has to sit on
					// the text baseline rather than start its own column.
					verticalAlign: 'middle',
					color: 'common.neutral50',
					'&:hover': {
						color: 'common.neutral20',
						backgroundColor: 'transparent',
					},
				}}
			>
				<InfoOutlinedIcon sx={{ fontSize: '18px' }} />
			</IconButton>
		</Tooltip>
	);
}
